/* iceberg-ui */
import { Box, Card, Flex } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { CircularProgress } from '@mui/material';
import { ReportParams } from 'application/Shared/types/reports';
import { useFetchTenantSetting } from 'hooks/dataAccess/Tenant/tenantSetting';

/* Hooks */
import { useReportParams } from 'hooks/useReportParams';

/*  React  */
import React from 'react';
import { useParams } from 'react-router';

/* Components */
import { AccountsInventoryReport } from '../AccountsInventory/AccountsInventoryReport';
import Adoption from '../Adoption/Adoption';
import { ApplicationMapReports } from '../ApplicationMapReports';
import { ApplicationOverlapReport } from '../ApplicationOverlap/ApplicationOverlapReport';
import { BrowserReport } from '../Browser/BrowserReport';
import BusinessOwnerReports from '../BusinessOwnerReports';
import CategoryReports from '../CategoryReports';
import { ContractsReport } from '../Contracts/ContractsReport';
import Discovery from '../Discovery/Discovery';
import LibraryReports from '../LibraryReports';
import MicrosoftOfficeOverlapReports from '../MicrosoftOfficeOverlapReports';
import { OffboardReport } from '../Offboard/Report';
import PartnerApplicationOverviewReports from '../PartnerApplicationOverviewReports';
import PartnerAppUsage from '../PartnerApps/Usage';
import PartnerUsersReport from '../PartnerUsers/PartnerUsersReport';
import { QBRFirstReports } from '../QBR/QBR';
import ShadowITReports from '../ShadowITReports';
import { StatusReports } from '../StatusReports';
import { UsageReports } from '../Usage/Usage';
import UserApps from '../UserApps/UserApps';
import VendorIncident from '../VendorIncident/Containers/Report';
import { ReportHeader } from './ReportHeader';

const RenderCurrentReport = ({
    renderingScheduledReport,
    reportData,
    reportSetup,
    subReportType
}: {
    renderingScheduledReport: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reportData: any;
    reportSetup: ReportParams;
    subReportType: string;
}) => {
    const sharedProps = {
        renderingScheduledReport,
        reportData: reportData[subReportType],
        reportSetup
    };

    switch (subReportType) {
        // Partner Reports - Application Overview - Applications Used Tab
        // Partner Reports - Application Overview - Application Usage Tab
        case 'ApplicationsUsed':
        case 'ApplicationUsage':
            return (
                <PartnerApplicationOverviewReports
                    {...sharedProps}
                    tab={subReportType}
                />
            );

        // Partner Report - Vendor Incident Report
        case 'ClientImpact':
            return <VendorIncident {...sharedProps} />;

        // Partner User Inventory Report
        case 'PartnerUsers':
            return <PartnerUsersReport {...sharedProps} />;

        //  Partner Application Usage Report, currently hidden
        case 'TopApplicationsUsed':
            return <PartnerAppUsage {...sharedProps} />;

        case 'AccountsInventory':
            return <AccountsInventoryReport {...sharedProps} />;

        // Admin Discovery Outcome Report
        case 'Discovery':
            return <Discovery {...sharedProps} />;

        // Admin Report - QBR Report
        case 'QBRFirst':
            return <QBRFirstReports {...sharedProps} />;

        // Admin Offboard Report and Workflow
        case 'UserOffboard':
            return <OffboardReport {...sharedProps} />;

        // Admin Report - Contracts Report
        case 'Contracts':
            return <ContractsReport {...sharedProps} />;

        // Admin Report - Browser Account Overview
        case 'Browser':
            return <BrowserReport {...sharedProps} />;

        // Admin Report - Organization Usage Report - Usage Reports Tab
        case 'Usage':
            return <UsageReports {...sharedProps} />;
        // Admin Report - Organization Usage Report - Library Report Tab

        case 'Library':
            return <LibraryReports {...sharedProps} />;

        // Admin Report - Organization Usage Report - Category Report Tab
        case 'Category':
            return <CategoryReports {...sharedProps} />;

        // Admin Report - Bussiness Owner Report
        case 'BusinessOwner':
            return <BusinessOwnerReports {...sharedProps} />;

        // Admin Report - Product Adoption Report
        case 'ProductAdoption':
            return <Adoption {...sharedProps} />;

        case 'ApplicationOverlap':
            return <ApplicationOverlapReport {...sharedProps} />;

        // Admin Report - Microsoft Office Overlap Report
        case 'MicrosoftOfficeOverlap':
            return <MicrosoftOfficeOverlapReports {...sharedProps} />;

        // Admin Report - Shadow IT Report
        case 'ShadowIT':
            return <ShadowITReports {...sharedProps} />;

        // Admin Report - Application Map Report
        case 'Map':
            return <ApplicationMapReports {...sharedProps} />;

        // Admin Report - User Application Inventory Report
        case 'UserApps':
            return <UserApps {...sharedProps} />;

        // Admin Report - Application Lifecycle Stage Breakdown Report - Application Lifecycle Stage Tab
        case 'ApplicationLifecycleStage':
            return <StatusReports {...sharedProps} />;

        default:
            return null;
    }
};

export const ChooseReportComponent = ({
    loadingReport,
    renderingScheduledReport,
    reportData,
    subReportType
}: {
    loadingReport: boolean;
    renderingScheduledReport: boolean;
    reportData: object | null;
    subReportType: string;
}) => {
    const { clientId, reportType } = useParams();
    const parsedReportParams = useReportParams(reportType);
    const { setting: qbrToCBR } = useFetchTenantSetting({
        clientId,
        key: 'qbrToCBR'
    });

    if (!subReportType || !parsedReportParams) return null;

    const { platformTenantId, startTime, endTime } = parsedReportParams;

    const reportName = qbrToCBR && reportType === 'qbr' ? 'cbr' : reportType;

    return (
        <Card
            className="printMoveMarginLeft"
            sx={{
                backgroundColor: 'background.reports',
                margin: '0 auto',
                maxWidth: '1280px',
                minHeight: 'calc(100vh - 256px);',
                padding: '24px',
                width: '100%'
            }}
            data-trackid="chooseReportComponentByTab"
        >
            <Box
                data-trackid={`chooseReportComponent_${subReportType}Report`}
                width="100%"
            >
                <ReportHeader
                    startDate={startTime}
                    endDate={endTime}
                    platformTenantId={platformTenantId}
                    reportType={reportName!}
                />
                {loadingReport && (
                    <Flex
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 'calc(100vh - 400px);',
                            width: '100%'
                        }}
                    >
                        <CircularProgress />
                    </Flex>
                )}
                {!loadingReport && reportData && (
                    <RenderCurrentReport
                        renderingScheduledReport={renderingScheduledReport}
                        reportData={reportData}
                        reportSetup={parsedReportParams}
                        subReportType={subReportType}
                    />
                )}
            </Box>
        </Card>
    );
};
